/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Button,
  Stack,
  TextField,
  Typography,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  LoadingButton,
} from '@mui/lab';
import { zodResolver } from '@hookform/resolvers/zod';
// eslint-disable-next-line import/no-extraneous-dependencies
import * as z from 'zod';
import {
  useForm,
  getFieldProps,
  getSubmitButtonProps,
  getFormProps,
  useFetch,
  useApiClient,
} from '@marageti/z4-lib';
import { PasswordConfig } from '@marageti/z4-sdk/lib/auth';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';

type ChangePasswordFormDefaultValues = {
  currentPassword: string | undefined,
  newPassword: string | undefined,
  confirmNewPassword: string | undefined,
};

const DEFAULT_VALUES = {
  currentPassword: '',
  newPassword: '',
  confirmNewPassword: '',
} as ChangePasswordFormDefaultValues;

const useValidationSchema = () => {
  const apiClient = useApiClient();
  const { data: passwordConfig } = useFetch<PasswordConfig>(apiClient.authClient.getPasswordConfig, [], true);

  const schema = z.object({
    currentPassword: z.string().min(1, 'Required'),
    newPassword: z.string().min(1, 'Please enter a new password')
      .refine(async (value) => {
        if (!passwordConfig) return false;
        const regex = new RegExp(passwordConfig.filter);
        return regex.test(value);
      }, {
        message: passwordConfig?.description,
      }),
    confirmNewPassword: z.string(),
  }).refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'Passwords do not match',
    path: ['confirmNewPassword'],
  }).refine((data) => data.currentPassword !== data.newPassword, {
    message: 'New password should not match the current password',
    path: ['newPassword'],
  });

  return schema;
};

type ChangePasswordFormProps = {
  onSubmit: (data: any, onSuccess: any) => void,
  submissionError: ApiError | false
  onCancel?: () => void
};

const ChangePasswordForm = ({
  onSubmit: submit,
  submissionError,
  onCancel = undefined,
}: ChangePasswordFormProps) => {
  const schema = useValidationSchema();
  const {
    register,
    handleSubmit: createSubmitHandler,
    formState: {
      isSubmitting, errors, isValid,
    },
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
  });

  const onSuccess = () => {
    reset(DEFAULT_VALUES);
  };

  const onSubmit = (data: any) => submit(data, onSuccess);

  return (
    <form {...getFormProps(createSubmitHandler(onSubmit))}>
      <Stack spacing={2}>
        <Stack useFlexGap>
          <Typography
            variant="h3"
            sx={{
              mb: 1,
            }}
          >
            Change Password
          </Typography>
          <TextField
            type="password"
            label="Current Password"
            {...register('currentPassword')}
            {...getFieldProps('currentPassword', errors)}
            autoComplete="current-password"
          />
          <TextField
            type="password"
            label="New Password"
            {...register('newPassword')}
            {...getFieldProps('newPassword', errors)}
            autoComplete="new-password"
          />
          <TextField
            type="password"
            label="Confirm New Password"
            {...register('confirmNewPassword')}
            {...getFieldProps('confirmNewPassword', errors)}
          />
        </Stack>

        {submissionError && (
          <Alert severity="error">
            <AlertTitle>There was an error submitting the form.</AlertTitle>
            {submissionError.message || 'Please try again.'}
          </Alert>
        )}

        <Stack
          direction="row"
          spacing={2}
          sx={(theme) => ({
            [theme.breakpoints.down('desktop')]: {
              marginTop: 'auto',
              flexDirection: 'column-reverse',
              '& > :not(:first-of-type)': {
                margin: theme.spacing(0, 0, 2.5, 0),
              },
            },
          })}
        >
          <LoadingButton
            variant="contained"
            type="submit"
            {...getSubmitButtonProps(isSubmitting, isValid)}
          >
            Save
          </LoadingButton>
          <Button
            variant="outlined"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};

export default ChangePasswordForm;
